/**
 * The app router factory. `createAppRouter` builds a TanStack Router over the route
 * tree with the live auth context (routing-and-state.md §9). Tests inject a
 * `createMemoryHistory` to drive navigation deterministically; main.tsx uses the
 * default browser history.
 */
import { createRouter, type RouterHistory } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

import { createRouterAuthContext } from './context.ts';
import { routeTree } from './routes.tsx';

export function createAppRouter(opts: {
  queryClient: QueryClient;
  history?: RouterHistory;
}): ReturnType<typeof buildRouter> {
  return buildRouter(opts);
}

function buildRouter(opts: { queryClient: QueryClient; history?: RouterHistory }) {
  return createRouter({
    routeTree,
    context: {
      queryClient: opts.queryClient,
      auth: createRouterAuthContext(),
    },
    ...(opts.history ? { history: opts.history } : {}),
    defaultPreload: false,
  });
}

// Register the router instance type so `useNavigate`/`<Link>`/`redirect` are typed to
// this app's routes across the codebase.
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
